import {useCallback} from 'react'
import {useNavigate} from 'react-router-dom'
import {useGlobalSnackbar} from '~/src/context/GlobalSnackbarContext'
import {useSwitchAgency, type SwitchedAgencyPermissions} from '~/src/hooks/useSwitchAgency'
import {useCurrentUser} from '~/src/store/slice-hooks'
import {useAppSelector} from '~/src/store/store-hooks'
import {reportCatchError} from '~/src/utils/errorReporter'
import {getRouteForRole} from '~/src/utils/routeUtils'
import type {NotificationDestination} from './notificationDestination'
import {invitationNotificationAccess} from './useInvitationNotificationAccess'

/**
 * Opens a notification's destination for the bell and the inbox. One that names a branch
 * (`agencyId`: an invitee's job order invitation sent to another of their branches, STFFLG-4184)
 * selects that branch first, as the header's switcher does (useSwitchAgency), so Pending Orders
 * lists the invitation. That branch's role then decides, as the route guard would: Pending Orders
 * when it opens there, else the role's first page (where the switcher lands). A branch that cannot
 * be selected leaves everything as it was, with the switcher's error toast.
 */
export function useOpenNotificationDestination(): (
  destination: NotificationDestination,
) => Promise<void> {
  const navigate = useNavigate()
  const switchAgency = useSwitchAgency()
  const {showError} = useGlobalSnackbar()
  const {currentUser} = useCurrentUser()
  const backendPermissions = useAppSelector((state) => state.root.backendRolePermissions)

  return useCallback(
    async ({path, state, agencyId}: NotificationDestination) => {
      if (!agencyId) {
        if (state) {
          navigate(path, {state})
        } else {
          navigate(path)
        }
        return
      }

      let permissions: SwitchedAgencyPermissions | undefined
      try {
        permissions = (await switchAgency(agencyId)).permissions
      } catch (switchError) {
        reportCatchError('Failed to switch agency:', switchError)
        showError('Failed to switch agency. Please try again.')
        return
      }

      // Without a recomputed tree (a general admin's one tree, or a failed read) the role's is the
      // one already in the store.
      const tree = permissions ? permissions.backendRolePermissions : backendPermissions
      if (invitationNotificationAccess(tree).canOpenPendingOrders) {
        navigate(path)
        return
      }
      navigate(
        permissions
          ? getRouteForRole(permissions.roleName, permissions.modulePermissions)
          : getRouteForRole(currentUser?.roles?.[0] || ''),
      )
    },
    [backendPermissions, currentUser?.roles, navigate, showError, switchAgency],
  )
}
